"use client";

import { useCallback, useEffect, useState } from "react";

// A tall screenshot is never shrunk below this share of the box width
// (it shows its top part instead), so its text doesn't get tiny.
const MIN_WIDTH_SHARE = "60cqw";

// Ref callback for a screenshot <img> inside a size container (the panel's
// inner box). Fits the whole screenshot in the box, never bigger than its
// real size, and not narrower than MIN_WIDTH_SHARE. Also works when the
// image was already loaded (cached) before React ran.
export const fitScreenshotToAppSize = (img) => {
    if (!img) return;
    const apply = () => {
        const { naturalWidth: w, naturalHeight: h } = img;
        if (!w || !h) return;
        img.style.width = `min(100cqw, ${w}px, max(100cqh * ${(w / h).toFixed(4)}, ${MIN_WIDTH_SHARE}))`;
    };
    if (img.complete) apply();
    else img.addEventListener("load", apply, { once: true });
};

// Vagaro-style card: a small screenshot card placed in one corner (or the
// middle) of the photo. Card size is a share of the photo panel (cq units).
const CARD_WIDTH = "92cqw";
const CARD_MAX_HEIGHT = "96cqh";

// Where the card sits and where the "Click to enlarge" pill goes (the
// corner away from the card), per layout position.
const CARD_POSITIONS = {
    center: { justifyContent: "center", alignItems: "center", pill: { right: "4%", bottom: "4%" } },
    "top-left": { justifyContent: "flex-start", alignItems: "flex-start", pill: { right: "4%", bottom: "4%" } },
    "top-right": { justifyContent: "flex-end", alignItems: "flex-start", pill: { left: "4%", bottom: "4%" } },
    "bottom-left": { justifyContent: "flex-start", alignItems: "flex-end", pill: { right: "4%", top: "4%" } },
    "bottom-right": { justifyContent: "flex-end", alignItems: "flex-end", pill: { left: "4%", top: "4%" } },
};

// Inline styles for a card layout (inline so they don't depend on new
// Tailwind classes being generated).
export const getScreenshotCardStyles = (position, { tight = false } = {}) => {
    const { pill, ...align } = CARD_POSITIONS[position] || CARD_POSITIONS.center;
    return {
        // Plain beige panels use a smaller margin so the card can be bigger.
        frame: { ...align, padding: tight ? "3.5%" : "6%", containerType: "size" },
        // The image itself is sized to fit (see fitScreenshotIntoCard), so the
        // card has no height cap that could clip it or its padding.
        card: {},
        pill,
    };
};

// Ref callback for the screenshot inside a card: sizes it so the whole
// screenshot fits in the card area, nothing cut off.
export const fitScreenshotIntoCard = (img) => fitScreenshot(img, CARD_WIDTH, CARD_MAX_HEIGHT);

// Same, for the bigger padded card on a plain beige panel (Vagaro-style):
// fills most of the panel, leaving room for the card's white padding.
export const fitLargeScreenshotIntoCard = (img) =>
    fitScreenshot(img, "calc(100cqw - 1.75rem)", "calc(100cqh - 1.75rem)");

const fitScreenshot = (img, maxWidth, maxHeight) => {
    if (!img) return;
    const apply = () => {
        const { naturalWidth: w, naturalHeight: h } = img;
        if (!w || !h) return;
        img.style.width = `min(${maxWidth}, calc(${maxHeight} * ${(w / h).toFixed(4)}))`;
    };
    if (img.complete) apply();
    else img.addEventListener("load", apply, { once: true });
};

// Open / close state for the full-screen screenshot viewer.
// Closes on Escape and stops the page from scrolling while open.
export const useFeatureImageZoomAction = () => {
    const [isOpen, setIsOpen] = useState(false);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        if (!isOpen) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") setIsOpen(false);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen]);

    return { isOpen, open, close };
};
